import assetUtils, { Asset } from 'app-lib/assetUtils'
import { asyncRetry, sleep } from 'app-lib/asyncUtils'
import errorUtils from 'app-lib/errorUtils'
import { approvalUtils } from 'app-lib/web3'
import _ from 'lodash'
import * as mobx from 'mobx'
import web3Utils from 'web3-utils'
import { BigNumber } from '@0x/utils'
import { chainConfigs, ChainId } from '../config/appConfig'
import RootStore from './RootStore'
import UserWallet from './UserWallet'

type SpendingApprovalsProps = 'pending' | 'error' | 'list' | 'isApproving'

export interface ISpendingApproval {
  amount: BigNumber
  assetAddress: string
  spender: string
  token: Asset
}

/**
 * Manage spending approvals
 * Approvals are in base unit.
 */
export default class SpendingApprovals {
  private rootStore: RootStore
  private userWallet: UserWallet
  public list: ISpendingApproval[] = []
  public error: Error | null = null
  public pending = false
  public isApproving: Asset | null = null

  /**
   * Helper to set values through mobx actions.
   */
  public set(prop: SpendingApprovalsProps, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  public assign(props: { [key: string]: any }) {
    Object.assign(this, props)
  }

  public clearError() {
    this.error = null
  }

  /**
   * If spender is an Asset, will return his address. Returns address as is in other cases.
   */
  public getSpenderAddress(spender: string | Asset) {
    return web3Utils.isAddress(spender)
      ? spender
      : assetUtils.getAddress(this.rootStore.fulcrumProvider.currentChainId, spender as Asset)
  }

  public findApproval(asset: Asset, spender: string | Asset) {
    const spenderAddress = this.getSpenderAddress(spender)
    return this.list.find(
      (approval) => approval.token === asset && approval.spender === spenderAddress
    )
  }

  /**
   * Get the current state for given approvals; does not update from blockchain
   */
  public findApprovals(assets: Asset[], spender: string | Asset) {
    const spenderAddress = this.getSpenderAddress(spender)
    return this.list.filter(
      (approval) => assets.includes(approval.token) && approval.spender === spenderAddress
    )
  }

  public needsApproval(
    chainId: ChainId,
    asset: Asset,
    spender: string | Asset,
    assetAmount: BigNumber
  ) {
    const isNative = assetUtils.isNativeTokenForNetwork(chainId, asset)
    if (isNative) {
      return false
    }
    const approval = this.findApproval(asset, spender)
    if (!approval) {
      return true
    }

    const decimals = assetUtils.getDecimals(chainId, asset)
    return approval.amount.div(10 ** decimals).lt(assetAmount)
  }

  public updateApprovalList(newApprovals: ISpendingApproval[]) {
    this.list = _.unionWith(
      newApprovals,
      this.list,
      (a, b) => a.token === b.token && a.spender === b.spender
    )
  }

  async checkApproval(token: Asset | string, spender: Asset | string) {
    const fp = this.rootStore.fulcrumProvider
    const approval = await approvalUtils.getSpendingApproval(fp, token, spender)
    this.updateApprovalList([approval])
    return approval
  }

  /**
   * Allows to check approval of multiple tokens for a single spender
   */
  async checkApprovals(assets: Asset[], spender: string) {
    try {
      this.assign({ pending: true, error: null })
      const fp = this.rootStore.fulcrumProvider
      const spenderAddress = this.getSpenderAddress(spender)
      const result = await asyncRetry(
        () => approvalUtils.getSpendingApprovalsOfAssets(fp, assets, spenderAddress),
        2,
        3000
      )
      this.updateApprovalList(result)
      return result
    } catch (err) {
      errorUtils.decorateError(err, { title: 'Spending Approvals Check Issue' })
      this.set('error', err)
      console.error(err)
    } finally {
      this.set('pending', false)
    }
  }

  async setInfiniteApprovalForToken(asset: Asset, spender: string | Asset) {
    try {
      this.assign({ isApproving: asset, error: null, pending: true })
      const fp = this.rootStore.fulcrumProvider
      const spenderAddress = this.getSpenderAddress(spender)
      await approvalUtils.setSpendingApprovalInfinite(fp, asset, spenderAddress)
      const newApprowal = await this.checkApproval(asset, spenderAddress)
      this.updateApprovalList([newApprowal])
      return newApprowal
    } catch (err) {
      errorUtils.decorateError(err, { title: 'Spending approval failed' })
      this.set('error', err)
      console.error(err)
      return false
    } finally {
      this.assign({ isApproving: null, pending: false })
    }
  }

  async setInfiniteApprovalForIToken(asset: Asset) {
    try {
      this.assign({ isApproving: asset, error: null, pending: true })
      const fp = this.rootStore.fulcrumProvider
      const iTokenAddress = assetUtils.getITokenAddress(fp.currentChainId, asset)
      await approvalUtils.setSpendingApprovalInfinite(fp, asset, iTokenAddress)
      const newApprowal = await this.checkApproval(asset, iTokenAddress)
      this.updateApprovalList([newApprowal])
      return newApprowal
    } catch (err) {
      errorUtils.decorateError(err, { title: 'Spending approval failed' })
      this.set('error', err)
      console.error(err)
      return false
    } finally {
      this.assign({ isApproving: null, pending: false })
    }
  }

  async removeApproval({ asset, spender }: { asset: Asset; spender: string | Asset }) {
    const { transactionStore, fulcrumProvider } = this.rootStore
    const txSteps = await approvalUtils.setSpendingApprovalErc20(fulcrumProvider, {
      erc20Token: asset,
      spender,
      amount: new BigNumber(0),
    })
    transactionStore.run('approval', txSteps)
  }

  async checkApprovalsOfAllItokens() {
    const { web3Connection } = this.rootStore
    const { chainId } = web3Connection
    if (chainId) {
      const tokens = chainConfigs[chainId].lending.assets
      const itokenAddresses = assetUtils.getITokenAddresses(chainId, tokens)

      for (const address of itokenAddresses) {
        await this.checkApprovals(tokens, address)
        await sleep(500)
      }
    }
  }

  clearData() {
    this.list = []
  }

  constructor(rootStore: RootStore) {
    this.userWallet = rootStore.userWallet
    this.rootStore = rootStore
    mobx.makeAutoObservable(this, undefined, { autoBind: true, deep: false })
  }
}
